import React, { useState } from "react";
import { X, Plus, Search, AlertTriangle } from "lucide-react";
import { Button } from "../shared/Button";

/**
 * PIEZAS COMPARTIDAS DE LOS MÓDULOS NUEVOS.
 *
 * Las veinte pantallas que se añadieron con el roadmap son, casi todas, la
 * misma forma: un encabezado con un botón, una barra de búsqueda, una lista y
 * un formulario en una ventana. Escribir esa forma veinte veces garantiza que
 * al mes no se parezcan entre sí.
 *
 * Se sigue el estilo del proyecto —estilos en el propio componente, clases en
 * español, variables `--*` del sistema de diseño— y se reusa `shared/Button`,
 * que ya existía.
 */

const est = {
  seccion: { display: "flex", flexDirection: "column", gap: "var(--space-5)" },

  cabecera: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--space-4)",
  },
  titulo: {
    margin: 0,
    fontFamily: "var(--font-display)",
    fontSize: "var(--text-xl)",
    color: "var(--text)",
  },
  subtitulo: {
    margin: "var(--space-1) 0 0",
    fontSize: "var(--text-sm)",
    color: "var(--text-secondary)",
  },

  buscador: { position: "relative", flex: "1 1 240px", maxWidth: "420px" },
  buscadorIcono: {
    position: "absolute",
    left: "var(--space-3)",
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--text-tertiary)",
  },
  buscadorCampo: {
    width: "100%",
    minHeight: "44px",
    padding: "var(--space-3) var(--space-4) var(--space-3) 2.5rem",
    borderRadius: "var(--radius-lg)",
    border: "2px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    color: "var(--text)",
    fontSize: "var(--text-base)",
  },

  /* La lista es una rejilla de tarjetas y no una tabla: en el teléfono de la
     caseta una tabla obliga a desplazar en horizontal, y ahí se pierde el
     dato justo cuando hay alguien esperando. */
  rejilla: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(280px, 100%), 1fr))",
    gap: "var(--space-4)",
  },
  tarjeta: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
    padding: "var(--space-4)",
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--border)",
    backgroundColor: "var(--bg-elevated)",
  },
  tarjetaTitulo: {
    margin: 0,
    fontSize: "var(--text-base)",
    fontWeight: 600,
    color: "var(--text)",
  },
  tarjetaDato: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    fontSize: "var(--text-sm)",
    color: "var(--text-secondary)",
  },
  tarjetaAcciones: {
    display: "flex",
    flexWrap: "wrap",
    gap: "var(--space-2)",
    marginTop: "var(--space-2)",
  },

  vacio: {
    padding: "var(--space-10) var(--space-4)",
    textAlign: "center",
    color: "var(--text-secondary)",
    border: "1px dashed var(--border)",
    borderRadius: "var(--radius-lg)",
  },

  velo: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "var(--space-4)",
    backgroundColor: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)",
  },
  ventana: {
    width: "100%",
    maxWidth: "560px",
    maxHeight: "90vh",
    overflowY: "auto",
    borderRadius: "var(--radius-xl)",
    backgroundColor: "var(--bg-elevated)",
    boxShadow: "var(--shadow-2xl)",
  },
  ventanaCabecera: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--space-4)",
    padding: "var(--space-5)",
    borderBottom: "1px solid var(--border)",
  },
  ventanaCuerpo: { padding: "var(--space-5)", display: "grid", gap: "var(--space-4)" },
  ventanaPie: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "var(--space-3)",
    padding: "var(--space-5)",
    borderTop: "1px solid var(--border)",
  },
  cerrar: {
    display: "grid",
    placeItems: "center",
    width: "40px",
    height: "40px",
    border: 0,
    borderRadius: "var(--radius-md)",
    background: "transparent",
    color: "var(--text-secondary)",
    cursor: "pointer",
  },

  campo: { display: "grid", gap: "var(--space-2)" },
  etiqueta: {
    fontSize: "var(--text-sm)",
    fontWeight: 500,
    color: "var(--text-secondary)",
  },
  control: {
    width: "100%",
    minHeight: "44px",
    padding: "var(--space-3)",
    borderRadius: "var(--radius-lg)",
    border: "2px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    color: "var(--text)",
    fontSize: "var(--text-base)",
  },
  fila: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
    gap: "var(--space-4)",
  },
  aviso: {
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--space-3)",
    padding: "var(--space-4)",
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--warning)",
    backgroundColor: "var(--warning-subtle, rgba(212,175,55,0.12))",
    color: "var(--text)",
    fontSize: "var(--text-sm)",
  },
};

export const estilos = est;

/** Encabezado de sección con acción principal opcional. */
export function Cabecera({ titulo, subtitulo, accion, alAccionar, icono: Icono = Plus }) {
  return (
    <div style={est.cabecera}>
      <div>
        <h2 style={est.titulo}>{titulo}</h2>
        {subtitulo && <p style={est.subtitulo}>{subtitulo}</p>}
      </div>
      {accion && (
        <Button onClick={alAccionar} leftIcon={Icono} variant="primary" size="md">
          {accion}
        </Button>
      )}
    </div>
  );
}

export function Buscador({ valor, alCambiar, marcador = "Buscar…" }) {
  return (
    <div style={est.buscador}>
      <Search size={18} style={est.buscadorIcono} />
      <input
        style={est.buscadorCampo}
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        placeholder={marcador}
      />
    </div>
  );
}

export function Vacio({ children }) {
  return <div style={est.vacio}>{children}</div>;
}

export function Aviso({ children }) {
  return (
    <div style={est.aviso}>
      <AlertTriangle size={18} style={{ flexShrink: 0, color: "var(--warning)" }} />
      <div>{children}</div>
    </div>
  );
}

export function Campo({ etiqueta, children }) {
  return (
    <label style={est.campo}>
      <span style={est.etiqueta}>{etiqueta}</span>
      {children}
    </label>
  );
}

export function Entrada({ etiqueta, ...props }) {
  return (
    <Campo etiqueta={etiqueta}>
      <input style={est.control} {...props} />
    </Campo>
  );
}

export function Seleccion({ etiqueta, opciones, ...props }) {
  return (
    <Campo etiqueta={etiqueta}>
      <select style={est.control} {...props}>
        {opciones.map((o) => (
          <option key={o.valor ?? o} value={o.valor ?? o}>
            {o.etiqueta ?? o}
          </option>
        ))}
      </select>
    </Campo>
  );
}

export function Area({ etiqueta, ...props }) {
  return (
    <Campo etiqueta={etiqueta}>
      <textarea style={{ ...est.control, minHeight: "96px", resize: "vertical" }} {...props} />
    </Campo>
  );
}

/**
 * Ventana modal.
 *
 * Se cierra con la X y con Escape, **no** tocando fuera: en una caseta, con el
 * teléfono en una mano, un toque accidental en el borde borraría un formulario
 * a medio llenar.
 */
export function Ventana({ titulo, abierta, alCerrar, children, pie }) {
  React.useEffect(() => {
    if (!abierta) return undefined;
    const alTeclear = (e) => e.key === "Escape" && alCerrar();
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [abierta, alCerrar]);

  if (!abierta) return null;

  return (
    <div style={est.velo} role="dialog" aria-modal="true" aria-label={titulo}>
      <div style={est.ventana}>
        <div style={est.ventanaCabecera}>
          <h3 style={{ ...est.titulo, fontSize: "var(--text-lg)" }}>{titulo}</h3>
          <button type="button" style={est.cerrar} onClick={alCerrar} aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>
        <div style={est.ventanaCuerpo}>{children}</div>
        {pie && <div style={est.ventanaPie}>{pie}</div>}
      </div>
    </div>
  );
}

/** Estado de carga y de error, para no repetirlo en cada pantalla. */
export function useCarga(fn, dependencias = []) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const recargar = React.useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setDatos(await fn());
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudo cargar la información");
    } finally {
      setCargando(false);
    }
  }, dependencias); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    recargar();
  }, [recargar]);

  return { datos, cargando, error, recargar, setDatos };
}
